import React from 'react';
import { Alert, AlertVariantType } from '../common/components/Alert';
import { Button } from '../common/components/Button';
import { TextPassage } from '../common/components/TextPassage';
import { Toolbar, ToolbarItem } from '../common/components/Toolbar';
import { ProgressBar } from '../common/components/ProgressBar';
import Table from '../common/components/Table';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import '../common/wrappers/SectionHomeWrapper.css';
import './AlibabaCloudFirewall.css';
import Paginator from '../common/tables/Paginator';
import AlibabaCloudService from '../../services/AlibabaCloudService';
import AlibabaAccessControl from '../../models/AlibabaAccessControl';
import AlibabaAddressBook from '../../models/AlibabaAddressBook';
import { Searchbar } from '../common/components/Searchbar';
interface AlibabaCloudFirewallState {
    isLoading: boolean;
    alertVisible: boolean;
    alertText: string;
    alertType: AlertVariantType;
    currentView: 'access-control' | 'address-books';
    accessControlFilter: 'outbound' | 'inbound';
    
    // Access Control data
    accessControlRules: AlibabaAccessControl[];
    filteredAccessControlRules: AlibabaAccessControl[];
    accessControlRulesOnPage: AlibabaAccessControl[];
    accessControlSearchTerm: string;
    
    // Address Books data
    addressBooks: AlibabaAddressBook[];
    filteredAddressBooks: AlibabaAddressBook[];
    addressBooksOnPage: AlibabaAddressBook[];
    addressBookSearchTerm: string;
}

export default class AlibabaCloudFirewall extends React.Component<{}, AlibabaCloudFirewallState> {
    alibabaClouService: AlibabaCloudService;
    timeoutId: NodeJS.Timeout;

    constructor(props: any) {
        super(props);

        this.alibabaClouService = new AlibabaCloudService();
        this.timeoutId = undefined;

        this.state = {
            isLoading: true,
            alertVisible: false,
            alertText: '',
            alertType: 'default',
            currentView: 'access-control',
            accessControlFilter: 'outbound',
            
            accessControlRules: [],
            filteredAccessControlRules: [],
            accessControlRulesOnPage: [],
            accessControlSearchTerm: '',
            
            addressBooks: [],
            filteredAddressBooks: [],
            addressBooksOnPage: [],
            addressBookSearchTerm: '',
        };
    }

    async componentDidMount() {
        try {
            await this.updateState();
        } catch (error) {
            const HTTP_INTERNAL_SERVER_ERROR = 500;
            if (error.status === HTTP_INTERNAL_SERVER_ERROR) {
                this.handleError(error, "Error gathering firewall data");
            }
        }
        this.setState({ isLoading: false });
    }

    updateState = async () => {
        try {
            const firewallData = await this.alibabaClouService.getFirewallData();
            
            const accessControlRules = [
                ...(firewallData[0]?.ACL_list_out || []).map(rule => ({
                    direction: 'Outbound',
                    acl: rule.ACLname,
                    name: rule.ACLname,
                    source: rule.Source,
                    destination: rule.Destination,
                    port: rule.Port,
                    action: rule.Action,
                    order: rule.Order.toString(),
                    acl_uuid: rule.acl_uuid
                })),
                ...(firewallData[0]?.ACL_list_in || []).map(rule => ({
                    direction: 'Inbound',
                    acl: rule.ACLname,
                    name: rule.ACLname,
                    source: rule.Source,
                    destination: rule.Destination,
                    port: rule.Port,
                    action: rule.Action,
                    order: rule.Order.toString(),
                    acl_uuid: rule.acl_uuid
                }))
            ];

            const addressBooks = (firewallData[0]?.address_books || []).map(book => ({
                name: book.Abook_name,
                type: book.Type,
                address_list: book.AddressList.join(', '), 
                group_uuid: book.Group_uuid
            }));

            this.setState({
                accessControlRules,
                addressBooks,
                filteredAddressBooks: addressBooks,
            }, () => {
                this.applyAccessControlFilter();
            });
        } catch (error) {
            console.error('Error fetching data:', error);
            this.handleError(error, "Error loading data");
        } 
    };

    applyAccessControlFilter = () => {
        const { accessControlRules, accessControlFilter, accessControlSearchTerm } = this.state;
        
        let filteredRules = accessControlRules.filter(rule => 
            rule.direction?.toLowerCase() === accessControlFilter
        );

        if(accessControlSearchTerm) {
            const searchLower = accessControlSearchTerm.toLowerCase();
            filteredRules = filteredRules.filter(rule =>
                rule.direction?.toLowerCase().includes(searchLower) ||
                rule.acl?.toLowerCase().includes(searchLower) ||
                rule.source?.toLowerCase().includes(searchLower) ||
                rule.destination?.toLowerCase().includes(searchLower) ||
                rule.port?.toLowerCase().includes(searchLower) ||
                rule.action?.toLowerCase().includes(searchLower) ||
                rule.order?.toLowerCase().includes(searchLower)
            );
        }

        this.setState({ filteredAccessControlRules: filteredRules });
    };

    handleAccessControlSearch = (searchTerm: string | React.ChangeEvent<HTMLInputElement>) => {
        const value = typeof searchTerm === 'string' ? searchTerm : searchTerm.target.value;
        this.setState({ accessControlSearchTerm: value }, () => {
            this.applyAccessControlFilter();
        });
    };

    applyAddressBookFilter = () => {
        const { addressBooks, addressBookSearchTerm } = this.state;

        let filtered = addressBooks;

        if (addressBookSearchTerm) {
            const searchLower = addressBookSearchTerm.toLowerCase();
            filtered = addressBooks.filter(book => 
                book.name?.toLowerCase().includes(searchLower) ||
                book.type?.toLowerCase().includes(searchLower) ||
                book.address_list?.toLowerCase().includes(searchLower)
            );
        }

        this.setState({ filteredAddressBooks: filtered })
    };

    handleAddressBookSearch = (searchTerm: string | React.ChangeEvent<HTMLInputElement>) => {
        const value = typeof searchTerm === 'string' ? searchTerm : searchTerm.target.value;
        this.setState({ addressBookSearchTerm: value}, () => {
            this.applyAddressBookFilter();
        });
    };

    handleViewChange = (view: 'access-control' | 'address-books') => {
        this.setState({ currentView: view });
    };

    handleAccessControlFilterChange = (filter: 'outbound' | 'inbound') => {
        this.setState({ accessControlFilter: filter }, () => {
            this.applyAccessControlFilter();
        });
    };

    handleRefreshClick = async () => {
        this.setState({ isLoading: true });
        try {
            await this.updateState();
        } catch (error) {
            const HTTP_INTERNAL_SERVER_ERROR = 500;
            if (error.status === HTTP_INTERNAL_SERVER_ERROR) {
                this.handleError(error, "Error refreshing firewall data");
            }
        }
        this.setState({ isLoading: false });
    };

    openNewRequest = () => {
        const baseUrl = process.env.REACT_APP_SERVICENOW_BASE_URL;
        const { currentView } = this.state;
        
        let sysId: string;
        if (currentView === 'access-control') {
            sysId = 'ea214cf03bbc76103c457f2eb3e45abe'; 
        } else {
            sysId = '364dc0d12b0532503a4cf366fc91bf2d'; 
        }
        
        const url = `${baseUrl}/ec?id=sc_cat_item&sys_id=${sysId}`;
        window.open(url, '_blank');
    };

    handleError = async (error, message = 'Your request could not be completed at this time. Please try again later.') => {
        this.setState({
            alertVisible: true,
            alertType: 'error',
            alertText: error.body?.message ?? message,
            isLoading: false,
        });

        this.handleSetTimeout();
        throw error;
    };

    handleAlertClose = () => {
        this.setState({
            alertVisible: false,
            alertText: '',
            alertType: 'default'
        });
    };

    handleSetTimeout = () => {
        if (this.timeoutId !== undefined) {
            window.clearTimeout(this.timeoutId);
        }

        this.timeoutId = setTimeout(this.handleAlertClose, 10000);
    };

    renderAccessControlTable() {
        const { accessControlRulesOnPage, accessControlSearchTerm } = this.state;
        
        const tableRows = accessControlRulesOnPage.map((rule) => (
            <Table.Row key={`${rule.acl}-${rule.order}`}>
                <>{rule.direction}</>
                <>{rule.acl}</>
                <>{rule.source}</>
                <>{rule.destination}</>
                <>{rule.port}</>
                <>{rule.action}</>
                <>{rule.order}</>
            </Table.Row>
        ));

        return (
            <>
                <Table
                    header={
                        <Toolbar>
                            <ToolbarItem>
                                <Button 
                                    variant={this.state.accessControlFilter === 'outbound' ? 'primary' : 'secondary'}
                                    onClick={() => this.handleAccessControlFilterChange('outbound')}
                                    size="small"
                                >
                                    Outbound
                                </Button>
                            </ToolbarItem>
                        <ToolbarItem>
                            <Button 
                                variant={this.state.accessControlFilter === 'inbound' ? 'primary' : 'secondary'}
                                onClick={() => this.handleAccessControlFilterChange('inbound')}
                                size="small"
                            >
                                Inbound
                            </Button>
                        </ToolbarItem>
                        <ToolbarItem>
                            <Searchbar
                                placeholder="Search..."
                                value={accessControlSearchTerm}
                                onSearch={this.handleAccessControlSearch}
                                onChange={this.handleAccessControlSearch}
                                condensed
                                smallHeight="32px"
                            />
                        </ToolbarItem>
                        <ToolbarItem right>
                            <Button variant="primary" size="small" onClick={this.openNewRequest}>
                                New Request
                            </Button>
                        </ToolbarItem>
                    </Toolbar>
                }
                footer={
                    <Paginator
                        data={this.state.filteredAccessControlRules}
                        onPageChange={(rules: AlibabaAccessControl[]) => {
                            this.setState({ accessControlRulesOnPage: rules });
                        }}
                    />
                }
            >
                <Table.Head>
                    <Table.Head.Row>
                        <Table.Head.Cell>Direction</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>ACL Name</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>Source</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>Destination</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>Port</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>Action</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>Order</Table.Head.Cell>
                    </Table.Head.Row>
                </Table.Head>
                <Table.Body>
                    {tableRows}
                </Table.Body>
            </Table>
            </>
        );
    }

    renderAddressBooksTable() {
        const { addressBooksOnPage, addressBookSearchTerm } = this.state;
        
        const tableRows = addressBooksOnPage.map((addressBook, index) => (
            <Table.Row key={`${addressBook.name}-${index}`}>
                <>{addressBook.name}</>
                <>{addressBook.type}</>
                <>{addressBook.address_list}</>
            </Table.Row>
        ));

        return (
            <>
                <Table
                    header={
                    <Toolbar>
                        <ToolbarItem>
                            <Searchbar
                                placeholder="Search..."
                                value={addressBookSearchTerm}
                                onSearch={this.handleAddressBookSearch}
                                onChange={this.handleAddressBookSearch}
                                condensed
                                smallHeight="32px"
                            />
                        </ToolbarItem>
                        <ToolbarItem right>
                            <Button variant="primary" size="small" onClick={this.openNewRequest}>
                                New Request
                            </Button>
                        </ToolbarItem>
                    </Toolbar>
                }
                footer={
                    <Paginator
                        data={this.state.filteredAddressBooks}
                        onPageChange={(books: AlibabaAddressBook[]) => {
                            this.setState({ addressBooksOnPage: books });
                        }}
                    />
                }
            >
                <Table.Head>
                    <Table.Head.Row>
                        <Table.Head.Cell>Name</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>Type</Table.Head.Cell>
                        <Table.Head.Cell className='em-u-text-align-center'>Address List</Table.Head.Cell>
                    </Table.Head.Row>
                </Table.Head>
                <Table.Body>
                    {tableRows}
                </Table.Body>
            </Table>
            </>
        );
    }

    render() {
        const { currentView, isLoading, alertVisible, alertText, alertType } = this.state;

        return (
            <SectionHomeWrapper title="Alibaba Cloud Firewall Management">
                <TextPassage>
                    <p>
                        Manage your Alibaba Cloud firewall access control rules and address books.
                    </p>
                </TextPassage>
                <br />

                {alertVisible && (
                    <Alert 
                        variant={alertType} 
                        onClose={this.handleAlertClose}
                        body={alertText}
                    >
                        {alertText}
                    </Alert>
                )}

                <div style={{ marginBottom: '20px' }}>
                    <Button 
                        variant={currentView === 'access-control' ? 'primary' : 'secondary'}
                        onClick={() => this.handleViewChange('access-control')}
                        size="medium"
                        style={{ marginRight: '10px' }}
                    >
                        Access Control
                    </Button>
                    <Button 
                        variant={currentView === 'address-books' ? 'primary' : 'secondary'}
                        onClick={() => this.handleViewChange('address-books')}
                        size="medium"
                    >
                        Address Books
                    </Button>
                </div>

                {isLoading ? (
                    <ProgressBar indeterminate label="Loading firewall data..." />
                ) : (
                    currentView === 'access-control' 
                        ? this.renderAccessControlTable()
                        : this.renderAddressBooksTable()
                )}
            </SectionHomeWrapper>
        );
    }
}